import React, { useState, useEffect } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Smartphone,
  Monitor,
  Globe,
  Apple,
} from 'lucide-react';
import { linksAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import Badge from './common/Badge';

const DetailedAnalyticsTable = ({ shortCode }) => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('clicked_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: currentPage, limit: 50, sortBy, sortOrder };
      const result = await linksAPI.getDetailedAnalytics(shortCode, params);
      if (result.success) {
        setAnalytics(result.data.analytics);
        setPagination(result.data.pagination);
      } else {
        setError(result.error || 'Failed to fetch analytics');
      }
    } catch {
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [shortCode, currentPage, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return <ChevronUp className="h-3.5 w-3.5 text-ink-300" />;
    }
    return sortOrder === 'ASC' ? (
      <ChevronUp className="h-3.5 w-3.5 text-brand-600" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5 text-brand-600" />
    );
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'android':
        return <Smartphone className="h-4 w-4 text-emerald-600" />;
      case 'ios':
        return <Apple className="h-4 w-4 text-sky-600" />;
      case 'desktop':
        return <Monitor className="h-4 w-4 text-violet-600" />;
      default:
        return <Globe className="h-4 w-4 text-ink-400" />;
    }
  };

  const getSourceInfo = (click) => {
    if (click.source === 'profile' && click.profile_page && click.profile_username) {
      return {
        name: `Profile: ${click.profile_page}`,
        type: 'profile',
        details: `@${click.profile_username}`,
      };
    }

    const referer = click.referer;
    if (!referer || referer === 'null' || referer === 'undefined') {
      return { name: 'Direct', type: 'direct' };
    }

    try {
      const url = new URL(referer);
      const hostname = url.hostname;
      if (hostname.includes('facebook.com')) return { name: 'Facebook', type: 'social' };
      if (hostname.includes('twitter.com') || hostname.includes('x.com')) return { name: 'Twitter/X', type: 'social' };
      if (hostname.includes('instagram.com')) return { name: 'Instagram', type: 'social' };
      if (hostname.includes('linkedin.com')) return { name: 'LinkedIn', type: 'social' };
      if (hostname.includes('youtube.com')) return { name: 'YouTube', type: 'social' };
      if (hostname.includes('tiktok.com')) return { name: 'TikTok', type: 'social' };
      if (hostname.includes('whatsapp.com')) return { name: 'WhatsApp', type: 'messaging' };
      if (hostname.includes('telegram.org')) return { name: 'Telegram', type: 'messaging' };
      if (hostname.includes('gmail.com') || hostname.includes('mail.google.com')) return { name: 'Gmail', type: 'email' };
      if (hostname.includes('outlook.com') || hostname.includes('hotmail.com')) return { name: 'Outlook', type: 'email' };
      if (hostname.includes('google.com')) return { name: 'Google Search', type: 'search' };
      if (hostname.includes('bing.com')) return { name: 'Bing Search', type: 'search' };
      return { name: hostname, type: 'other' };
    } catch {
      return { name: 'Unknown', type: 'unknown' };
    }
  };

  const sourceVariant = (type) => {
    switch (type) {
      case 'social':
        return 'primary';
      case 'messaging':
        return 'success';
      case 'email':
        return 'purple';
      case 'search':
        return 'warning';
      case 'profile':
        return 'info';
      case 'direct':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-10 shadow-soft">
        <div className="flex justify-center">
          <LoadingSpinner text="Loading clicks…" />
        </div>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white shadow-soft overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">Click stream</h2>
          <p className="text-xs text-ink-500">
            Every click, sorted by{' '}
            <span className="text-ink-700">
              {sortBy.replace('_', ' ')} {sortOrder.toLowerCase()}
            </span>
          </p>
        </div>
        <p className="text-xs text-ink-500">
          {analytics.length} of {pagination.total || 0}
        </p>
      </div>

      {analytics.length === 0 ? (
        <div className="text-center py-12 text-sm text-ink-500">
          No clicks recorded yet.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50/70 border-b border-ink-200">
                <tr>
                  {[
                    { key: 'clicked_at', label: 'Date & time' },
                    { key: 'platform', label: 'Platform' },
                    { key: 'device_type', label: 'Device' },
                    { key: 'referer', label: 'Source' },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 py-3 text-left text-[11px] font-semibold text-ink-500 uppercase tracking-wider cursor-pointer hover:text-ink-700 select-none"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {col.label}
                        <SortIcon field={col.key} />
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
                    Visitor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {analytics.map((click, index) => {
                  const sourceInfo = getSourceInfo(click);
                  return (
                    <tr
                      key={click.id || index}
                      className="hover:bg-ink-50/60 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-ink-800">
                        {formatDate(click.clicked_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {getPlatformIcon(click.platform)}
                          <span className="capitalize text-ink-800">
                            {click.platform || 'Unknown'}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-ink-700 capitalize">
                        {click.device_type || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <Badge variant={sourceVariant(sourceInfo.type)} dot>
                              {sourceInfo.name}
                            </Badge>
                            {click.referer &&
                              click.referer !== 'null' &&
                              click.referer !== 'undefined' &&
                              sourceInfo.type !== 'profile' && (
                                <a
                                  href={click.referer}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-ink-400 hover:text-ink-700"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                          </div>
                          {sourceInfo.details && (
                            <span className="text-xs text-ink-500">
                              {sourceInfo.details}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-ink-500 font-mono text-xs">
                        {click.ip_hash ? click.ip_hash.slice(0, 10) : 'Unknown'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-ink-100">
              <p className="text-xs text-ink-500">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage(
                      Math.min(pagination.totalPages, currentPage + 1)
                    )
                  }
                  disabled={currentPage === pagination.totalPages}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DetailedAnalyticsTable;
